import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  Box,
  Divider,
  Rating,
  useMediaQuery,
  useTheme,
} from '@mui/material';

const professionalColors = [
  '#1E88E5', '#43A047', '#6D4C41', '#8E24AA',
  '#FB8C00', '#3949AB', '#00897B', '#546E7A',
];

const getProfessionalColor = (username) => {
  if (!username) return '#546E7A';
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % professionalColors.length;
  return professionalColors[index];
};

const FeedbackSection = ({ productId }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [newFeedback, setNewFeedback] = useState('');
  const [rating, setRating] = useState(0);
  const [averageRating, setAverageRating] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchFeedbacks = async () => {
      try {
        const res = await fetch(`https://reviewhub-lc.onrender.com/api/feedback/${productId}`);
        const data = await res.json();
        setFeedbacks(data);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchAverageRating = async () => {
      try {
        const res = await fetch(`https://reviewhub-lc.onrender.com/api/feedback/${productId}/average-rating`);
        const data = await res.json();
        setAverageRating(data.averageRating);
      } catch (err) {
        console.error(err);
      }
    };

    fetchFeedbacks();
    fetchAverageRating();
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const username = localStorage.getItem('username') || 'Anonymous';

    if (!rating) {
      alert('Please select a rating before submitting feedback.');
      return;
    }

    try {
      await fetch(`https://reviewhub-lc.onrender.com/api/add-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId, feedback: newFeedback, username, rating }),
      });

      setNewFeedback('');
      setRating(0);

      const res = await fetch(`https://reviewhub-lc.onrender.com/api/feedback/${productId}`);
      const data = await res.json();
      setFeedbacks(data);

      const avgRes = await fetch(`https://reviewhub-lc.onrender.com/api/feedback/${productId}/average-rating`);
      const avgData = await avgRes.json();
      setAverageRating(avgData.averageRating);
    } catch (err) {
      console.error(err);
    }
  };

  return (
<Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
<Box sx={{ width: '100%' }}>


 
        <Typography variant={isMobile ? 'h6' : 'h5'} gutterBottom>
          Feedback
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 2, fontStyle: 'italic' }}
        >
          Was this product review helpful or honest based on your experience?
        </Typography>

        {averageRating !== null && (
          <Box sx={{ display: 'flex', mb: 2 }}>
            <Rating value={Number(averageRating)} precision={0.1} readOnly />
            <Typography sx={{ ml: 1, fontSize: isMobile ? '0.9rem' : '1rem' }}>
              {averageRating} / 5
            </Typography>
          </Box>
        )}

        {/* Feedback Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Write your feedback"
            multiline
            rows={isMobile ? 3 : 4}
            value={newFeedback}
            onChange={(e) => setNewFeedback(e.target.value)}
            fullWidth
            required
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
          />

          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Your Rating:
            </Typography>
            <Rating
              name="user-rating"
              value={rating}
              onChange={(e, newValue) => setRating(newValue)}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
          >
            Submit
          </Button>
        </Box>

        {/* Feedback List */}
        <List sx={{ mt: 4 }}>
          {Array.isArray(feedbacks) && feedbacks.length > 0 ? (
            feedbacks.map((fb, index) => {
              const username = fb.username || 'Anonymous';
              const color = getProfessionalColor(username);
              return (
                <Box key={index}>
                  <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: color, color: '#fff' }}>
                        {username.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color,
                              fontWeight: 'bold',
                              textTransform: 'capitalize',
                              mr: 1,
                              fontSize: isMobile ? '0.85rem' : '1rem',
                            }}
                          >
                            {username}
                          </Typography>
                          <Rating value={fb.rating} readOnly size="small" />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-line',
                            fontSize: isMobile ? '0.85rem' : '0.95rem',
                            mt: 0.5,
                          }}
                        >
                          {fb.feedback}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index !== feedbacks.length - 1 && (
                    <Divider variant="inset" component="li" sx={{ ml: 7 }} />
                  )}
                </Box>
              );
            })
          ) : (
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 2, fontSize: isMobile ? '0.9rem' : '1rem' }}
            >
              No feedback yet.
            </Typography>
          )}
        </List>

      </Box>
    </Container>
  );
};

export default FeedbackSection;
